import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { ApplicationError } from '../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { ResponseObj } from '../../lib-api/services/base';
import {
  consumeHandoffSession,
  ConsumeHandoffSessionPayload,
  createUrlHandoffSession,
  CreateUrlHandoffSessionRequest,
  getHandoffSession,
  GetHandoffSessionPayload,
  updateHandoffSession,
  UpdateHandoffSessionPayload,
} from '../../lib-api/services/HandoffSession';
import { ConsumedHandoffSession, HandoffSession } from '../../models/handoffSession';
import { CommonError } from '../../stores/app-state-errors';

export interface HandoffSessionClient {
  consumeHandoffSession: ({
    handoffSessionId,
  }: ConsumeHandoffSessionPayload) => Promise<ResponseObj<ConsumedHandoffSession | undefined>>;
  getHandoffSession: ({
    handoffSessionId,
  }: GetHandoffSessionPayload) => Promise<ResponseObj<HandoffSession | undefined>>;
  createUrlHandoffSession: (
    payload: CreateUrlHandoffSessionRequest
  ) => Promise<ResponseObj<HandoffSession | undefined>>;
  updateHandoffSession: (
    payload: UpdateHandoffSessionPayload
  ) => Promise<ResponseObj<HandoffSession | undefined>>;
}

export const handoffSessionClient: HandoffSessionClient = {
  consumeHandoffSession,
  getHandoffSession,
  createUrlHandoffSession,
  updateHandoffSession,
};

export type HandoffSessionService = {
  createUrlHandoffSession: (payload: CreateUrlHandoffSessionRequest) => Promise<HandoffSession>;
  consumeUrlHandoffSession: (handoffSessionId: string) => Promise<ConsumedHandoffSession>;
  getHandoffSession: (handoffSessionId: string) => Promise<HandoffSession>;
  assignCredentials: (_: UpdateHandoffSessionPayload) => Promise<HandoffSession>;
};

export const ERR_HANDOFF_SESSION_FAILED_CONSUME_SESSION: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_CONSUME_HANDOFF_SESSION,
};

export const ERR_HANDOFF_SESSION_FAILED_GET_SESSION: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_HANDOFF_SESSION,
};

export const ERR_HANDOFF_CREATE_SESSION: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_CREATE_HANDOFF_SESSION,
};

export const ERR_HANDOFF_UPDATE_SESSION: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_UPDATE_HANDOFF_SESSION,
};

export const createHandoffSessionService = (
  client: HandoffSessionClient
): HandoffSessionService => ({
  async createUrlHandoffSession(payload) {
    const response = await client.createUrlHandoffSession(payload);

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(ERR_HANDOFF_CREATE_SESSION);
    }

    return response.data;
  },
  async getHandoffSession(handoffSessionId) {
    const response = await client.getHandoffSession({ handoffSessionId });

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(ERR_HANDOFF_SESSION_FAILED_GET_SESSION);
    }

    return response.data;
  },
  async consumeUrlHandoffSession(handoffSessionId) {
    const response = await client.consumeHandoffSession({ handoffSessionId });

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(ERR_HANDOFF_SESSION_FAILED_CONSUME_SESSION);
    }

    return response.data;
  },
  async assignCredentials({ handoffSessionId, credentialsId }) {
    const response = await client.updateHandoffSession({ handoffSessionId, credentialsId });

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(ERR_HANDOFF_UPDATE_SESSION);
    }

    return response.data;
  },
});

export const handoffSessionService = createHandoffSessionService(handoffSessionClient);
